import { useSelector } from 'react-redux';
import { authSelectors } from '../ducks/auth';
import { UserRole } from '../ducks/auth/types';
import { Permission, Resource, hasPermission, getPermissions } from '../constants/permissions';

export const usePermissions = () => {
  const loggedInUser = useSelector(authSelectors.loggedInUser);
  const userRole = loggedInUser?.role || UserRole.NORMAL_USER;

  const can = (resource: Resource, permission: Permission): boolean => {
    return hasPermission(userRole, resource, permission);
  };

  const canCreate = (resource: Resource): boolean => {
    return can(resource, Permission.CREATE);
  };

  const canRead = (resource: Resource): boolean => {
    return can(resource, Permission.READ);
  };

  const canUpdate = (resource: Resource): boolean => {
    return can(resource, Permission.UPDATE);
  };

  const canDelete = (resource: Resource): boolean => {
    return can(resource, Permission.DELETE);
  };

  const canExport = (resource: Resource): boolean => {
    return can(resource, Permission.EXPORT);
  };

  const canManageUsers = (): boolean => {
    return can(Resource.MEMBERS, Permission.MANAGE_USERS);
  };

  const canViewAnalytics = (): boolean => {
    return can(Resource.DASHBOARD, Permission.VIEW_ANALYTICS);
  };

  const getResourcePermissions = (resource: Resource): Permission[] => {
    return getPermissions(userRole, resource);
  };

  const hasAnyPermission = (resource: Resource, permissions: Permission[]): boolean => {
    return permissions.some(permission => can(resource, permission));
  };

  const hasAllPermissions = (resource: Resource, permissions: Permission[]): boolean => {
    return permissions.every(permission => can(resource, permission));
  };

  return {
    userRole,
    loggedInUser,
    can,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    canExport,
    canManageUsers,
    canViewAnalytics,
    getResourcePermissions,
    hasAnyPermission,
    hasAllPermissions,
  };
}; 